import api from "../api/api";
import { practiceDepartments, practiceFeatures } from "../config/practice";

/*
 * Dashboard Refresh for 9928 (and any other practice without syncRefresh,
 * except the retired 1111): v1 POST /v1/{p}/appointments/refresh/apt pulls
 * TODAY's booked appointments for ONE Athena department, one paid Athena call
 * per department.
 *
 * Which departments it pulls (2026-10-06: the Frisco Zocdoc booking for
 * patient 14112 stayed missing after Refresh):
 *   - "All", for an all_departments account. Its dashboard has no location
 *     partition (server helpers.get_scoped_departmentid -> None), so it lists
 *     both locations whatever the header says. Refresh pulls every location
 *     the header offers, one call after the other.
 *   - Otherwise the location selected in the header. WorkingLocationSelect
 *     shows and switches config.departmentid (PUT /user/switch-department
 *     writes it and the store follows), and the server scopes this user's
 *     dashboard to exactly that location.
 *   - No location stored: Odessa ("1"). The header shows that, and the server
 *     scopes an untagged user to it (the 2026-08-13 fail-closed rule).
 * Only the header's own locations (config/practice.js departments, the list
 * the switcher offers) are ever refreshed. A stored value outside them
 * refreshes nothing.
 *
 * A practice without the header switcher (one location) keeps the old body:
 * its stored department, the server defaulting to 1 when there is none.
 */

// The public cron key the route checks (already in every care bundle).
const REFRESH_APT_APIKEY = "127d196f773179685bb945abb7a8f781";

// Department ids (strings, like the header's) Refresh pulls for this config.
export function refreshDepartmentIds(config) {
  if (!practiceFeatures().workingLocations) return [config?.departmentid];
  const locations = practiceDepartments().map((d) => String(d.id));
  if (config?.all_departments) return locations;
  const selected = String(config?.departmentid || "1");
  return locations.includes(selected) ? [selected] : [];
}

const locationName = (id) =>
  (practiceDepartments().find((d) => String(d.id) === String(id)) || {}).name || `Department ${id}`;

// Refreshes each department in turn. Resolves to a response-shaped body for
// the dashboard toast: { status: 200, count, failedLocations } once at least
// one department refreshed (count = the sum of their counts, or null when no
// answer had one). Rejects when none did, with the first error, as the single
// call did before.
export async function refreshAppointmentsForLocation(practiseid, config) {
  const ids = refreshDepartmentIds(config);
  if (!ids.length) throw new Error("Refresh: no location the header offers is selected");
  let count = null;
  let refreshed = 0;
  let firstError = null;
  const failedLocations = [];
  for (const id of ids) {
    try {
      const res = await api.post(`/v1/${practiseid}/appointments/refresh/apt`, {
        apikey: REFRESH_APT_APIKEY,
        department_id: id,
      });
      if (res?.status !== 200) throw new Error(`refresh/apt status ${res?.status}`);
      refreshed += 1;
      if (res?.count != null) count = (count || 0) + (Number(res.count) || 0);
    } catch (error) {
      if (!firstError) firstError = error;
      failedLocations.push(locationName(id));
    }
  }
  if (!refreshed) throw firstError;
  return { status: 200, count, failedLocations };
}
